import { Card, Collapse, Flex, Input, Space, Typography } from 'antd';
import React, { useEffect, useState } from 'react';

import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import {
    avaArea,
    polygonDetailsSelector,
    selectedIndicesState,
    setBackInput
} from "../ThreeJs/ReactStates/states";
import { themeState } from "../lib/atom";
import { CaretRightOutlined } from "@ant-design/icons";

import { POLYGONS } from "../ThreeJs/Global/variables";
import { updateAvaArea, updateSetbackUI } from "../ThreeJs/Segments/SetBacks/Functions";
import { updatePolygonSetbacksSelector } from '../ThreeJs/ReactStates/updates';
import { indexToLabel } from '../ThreeJs/Segments/Labels';

const SegmentDetail = () => {
    const { Panel } = Collapse;

    // Recoil state and selectors to access current theme, selected segment indices, polygon details, and other states.
    const theme = useRecoilState(themeState)[0];
    const details = useRecoilValue(polygonDetailsSelector);  // pitch, azimuth, area
    const selectedIndices = useRecoilValue(selectedIndicesState);  // Selected segment index
    const [inputValues, setInputValues] = useRecoilState(setBackInput);  // setback inputs
    const addSetback = useSetRecoilState(updatePolygonSetbacksSelector);
    const [inputValue, setInputValue] = useState(0);
    const [availableArea, setAvailableArea] = useRecoilState(avaArea);  // setback area for selected segment
    const [userChanged, setUserChanged] = useState(false);
    const [indexChanged, setIndexChanged] = useState(null);

    // ---------- INIT SETBACK INPUTS ON SEGMENT CHANGE ----------
    useEffect(() => {
        if (selectedIndices.segmentIndex !== null) {
            const seg = POLYGONS[selectedIndices.segmentIndex];
            if (!seg || !seg.setBacks || !seg.setBacks.edgesData) return;

            const initialInputValues = {};
            seg.setBacks.edgesData.forEach((edge, index) => {
                initialInputValues[index] = edge.value;
            });
            setInputValues(initialInputValues);
        }
    }, [selectedIndices.segmentIndex]);

    // ---------- APPLY SETBACK VALUE CHANGES ----------
    useEffect(() => {
        if (inputValue >= 0 && userChanged) {
            const index = selectedIndices.segmentIndex;
            if (index === null || index === undefined) {
                setUserChanged(false);
                return;
            }

            addSetback({ setbackAdded: true, segmentIndex: index });
            updateSetbackUI(index, setInputValues, Number(inputValue), indexChanged);
            updateAvaArea(index, setAvailableArea);
        }
        setUserChanged(false);
    }, [inputValue]);

    // ---------- HANDLERS ----------
    // Uniform setback (all edges)
    const handleInputChange = (e) => {
        const value = e.target.value;
        if (/^\d*$/.test(value)) {  // numeric only
            setIndexChanged(undefined);
            setInputValue(value);
            setUserChanged(true);
        }
    };

    // Single-edge setback
    const handleSingleInputChange = (e, index) => {
        const value = e.target.value;
        if (/^\d*$/.test(value)) {
            setIndexChanged(index);
            setInputValue(value);
            setUserChanged(true);
        }
    };

    // ---------- PREPARE SETBACK INPUT UI ----------
    const setbacks = [];
    for (let [index, value] of Object.entries(inputValues)) {
        const label = indexToLabel(index).toLowerCase();
        setbacks.push(
            <Flex style={{ padding: '0 14px 12px 40px' }} key={index} justify='space-between'>
                <Typography.Text
                    style={{ display: 'inline-block', width: '20px' }}
                    className={`${theme ? 'text-gray' : 'text-gray-1'}`}
                >
                    <b>{label}</b>
                </Typography.Text>
                <Input
                    size='small'
                    style={{ width: 45, borderColor: theme ? '#555' : '#ccc' }}
                    className={`${theme ? 'text-gray' : 'text-gray-1'}`}
                    onChange={(e) => handleSingleInputChange(e, index)}
                    value={value}
                />
                <span></span>
                <span></span>
            </Flex>
        );
    }

    // ---------- DERIVED STATS (PANELS + AREAS) ----------
    const segmentIndex = selectedIndices.segmentIndex;
    const segment = segmentIndex !== null ? POLYGONS[segmentIndex] : null;

    // Total segment area (sqft)
    const totalAreaSqft =
        details.area !== null && details.area !== undefined
            ? parseFloat(details.area)
            : 0;

    // Panels info
    const panels = segment?.panels || [];
    const panelsCount = panels.length;

    // Normalize panel area to sqft (supports both manual placement + autoPopulate)
    const PANEL_M2_TO_SQFT = 10.7639;
    const totalPanelsAreaSqft = panels.reduce((sum, p) => {
        let areaSqft = 0;

        // Prefer geometry if available
        if (p.panel && p.panel.geometry && p.panel.geometry.parameters) {
            const { width, height } = p.panel.geometry.parameters; // meters
            if (typeof width === 'number' && typeof height === 'number') {
                areaSqft = width * height * PANEL_M2_TO_SQFT;
            }
        } else if (typeof p.area === 'number') {
            // Heuristic: small = m², large = ft² (for compatibility with existing code)
            if (p.area < 30) {
                areaSqft = p.area * PANEL_M2_TO_SQFT; // m² -> ft²
            } else {
                areaSqft = p.area; // already ft²
            }
        }

        return sum + areaSqft;
    }, 0);

    const hasPanels = panelsCount > 0;

    // Setback area (sqft) – from recoil state (set in updateAvaArea)
    const setbackAreaSqft =
        availableArea !== '' && availableArea !== null && availableArea !== undefined
            ? parseFloat(availableArea)
            : null;

    const hasSetbacks =
        !!segment &&
        !!segment.setBacks &&
        !!segment.setBacks.isSet &&
        Array.isArray(segment.setBacks.edgesData) &&
        segment.setBacks.edgesData.some(e => (e.value || 0) > 0) &&
        setbackAreaSqft !== null &&
        !Number.isNaN(setbackAreaSqft);

    // Available area after panels (no setbacks)
    const availableAfterPanelsSqft =
        !hasSetbacks && hasPanels
            ? Math.max(0, totalAreaSqft - totalPanelsAreaSqft)
            : null;

    // Available area after setback + panels
    const availableAfterSetbackAndPanelsSqft =
        hasSetbacks && hasPanels
            ? Math.max(0, setbackAreaSqft - totalPanelsAreaSqft)
            : null;

    return (
        <div>
            <Card
                style={{ fontSize: '16px' }}
                bodyStyle={{ padding: 0, fontSize: '16px' }}
                className='segment__card'
            >
                <Flex
                    vertical
                    style={{ maxHeight: '260px', overflow: 'auto', overflowX: 'hidden' }}
                    className={`${theme ? 'custom-scrollbar-dark' : 'custom-scrollbar'}`}
                >
                    {/* ---- Base segment info ---- */}
                    <Flex justify='space-between' align='center' style={{ padding: '14px' }}>
                        <Space.Compact direction='vertical'>
                            <Typography.Text strong>
                                Pitch
                            </Typography.Text>
                            <Typography.Text style={{ fontSize: '12px' }}>
                                {details.pitch !== null ? `${details.pitch}°` : 'N/A'}
                            </Typography.Text>
                        </Space.Compact>

                        <Space.Compact direction='vertical'>
                            <Typography.Text strong>
                                Azimuth
                            </Typography.Text>
                            <Typography.Text style={{ fontSize: '12px' }}>
                                {details.azimu !== null ? `${details.azimu}°` : 'N/A'}
                            </Typography.Text>
                        </Space.Compact>

                        <Space.Compact direction='vertical'>
                            <Typography.Text strong>
                                Total Area
                            </Typography.Text>
                            <Typography.Text style={{ fontSize: '12px' }}>
                                {totalAreaSqft ? `${totalAreaSqft.toFixed(2)} sqft` : 'N/A'}
                            </Typography.Text>
                        </Space.Compact>
                    </Flex>

                    {/* ---- Setback & panel coverage stats ---- */}
                    {(hasSetbacks || hasPanels) && (
                        <Space.Compact
                            direction='vertical'
                            style={{ padding: '0 0 8px 14px' }}
                        >
                            {/* Available Area after Setback */}
                            {hasSetbacks && (
                                <>
                                    <Typography.Text strong>
                                        Available Area after Setback
                                    </Typography.Text>
                                    <Typography.Text style={{ fontSize: '12px' }}>
                                        {`${setbackAreaSqft.toFixed(2)} sqft`}
                                    </Typography.Text>
                                </>
                            )}

                            {/* Panels summary */}
                            {hasPanels && (
                                <>
                                    <Typography.Text strong style={{ marginTop: 8 }}>
                                        Panels
                                    </Typography.Text>
                                    <Typography.Text style={{ fontSize: '12px' }}>
                                        {`Number of panels placed: ${panelsCount}`}
                                    </Typography.Text>
                                    <Typography.Text style={{ fontSize: '12px' }}>
                                        {`Area covered by panels: ${totalPanelsAreaSqft.toFixed(2)} sqft`}
                                    </Typography.Text>

                                    {hasSetbacks && availableAfterSetbackAndPanelsSqft !== null && (
                                        <Typography.Text style={{ fontSize: '12px' }}>
                                            {`Available Area after Setback + Panels: ${availableAfterSetbackAndPanelsSqft.toFixed(2)} sqft`}
                                        </Typography.Text>
                                    )}

                                    {!hasSetbacks && availableAfterPanelsSqft !== null && (
                                        <Typography.Text style={{ fontSize: '12px' }}>
                                            {`Available Area after Panels: ${availableAfterPanelsSqft.toFixed(2)} sqft`}
                                        </Typography.Text>
                                    )}
                                </>
                            )}
                        </Space.Compact>
                    )}

                    {/* ---- Setbacks inputs ---- */}
                    <Collapse
                        bordered={false}
                        expandIcon={({ isActive }) =>
                            <CaretRightOutlined style={{ color: '#ccc' }} rotate={isActive ? 90 : 0} />
                        }
                    >
                        <Panel
                            header={<Typography.Text strong>Setbacks (in)</Typography.Text>}
                            key="1"
                            extra={
                                // Uniform setback input
                                <Input
                                    style={{ width: 45, borderColor: theme ? '#555' : '#ccc' }}
                                    className={`${theme ? 'text-gray' : 'text-gray-1'}`}
                                    onClick={event => event.stopPropagation()}
                                    onChange={handleInputChange}
                                    value={
                                        Object.values(inputValues).length > 0 &&
                                        Object.values(inputValues).every(
                                            val => val === Object.values(inputValues)[0]
                                        )
                                            ? Object.values(inputValues)[0]
                                            : null
                                    }
                                />
                            }
                        >
                            {setbacks}
                        </Panel>
                    </Collapse>
                </Flex>
            </Card>
        </div>
    );
};

export default SegmentDetail;
